import { cloneDeep } from 'lodash';
import moment from 'moment';
import { Period } from '../../../../interfaces/redux.interface';
import {
	CostCompareDepartmentModel,
	CostCompareEmployeeModel,
} from '../../../../models/costingComparativeReport.model';
import httpService from '../../../../services/http.service';
import {
	getDateFrom,
	getPeriodNumber,
	parseLastDate,
} from '../../../../utils/reportsPagesHelpers.utils';
import MonthYearFormat from '../../../FromMonthToMonthReports/ByEmployeeform/Formats/monthYearFormat';

export type FilterState = {
	period: Period[];
	dep: string[];
	emp: string[];
	from_month: number[];
	from_year: number[];
	to_month: number[];
	to_year: number[];
	change_percent: boolean[];
	periods: string[];
	unitIds: number[];
};

export const initialFilterState: FilterState = {
	period: [Period.MONTH],
	dep: [],
	emp: [],
	from_month: [],
	from_year: [],
	to_month: [],
	to_year: [],
	change_percent: [false],
	periods: [],
	unitIds: [],
};

export const initialState: CompareReportReducerState = {
	filters: initialFilterState,
	departments: [],
	employees: [],
	periods: [],
	errorMessage: '',
	lastMonth: [],
	lastYear: [],
	completed: true,
	totalDepartmentsReports: undefined,
	sortDepartmentsBy: null,
	departmentsSortDirection: null,
	sortEmployeesBy: null,
	employeesSortDirection: null,
	selectedDepartmentForEmployeesFetch: [],
	dateChanged: '',
	originalPeriods: [],
};

export type CompareReportReducerState = {
	filters: FilterState;
	departments: any[];
	employees: any[];
	errorMessage: string;
	completed: boolean;
	totalDepartmentsReports?: number;
	sortDepartmentsBy: string | null;
	departmentsSortDirection: string | null;
	sortEmployeesBy: string | null;
	employeesSortDirection: string | null;
	periods: string[];
	selectedDepartmentForEmployeesFetch: string[];
	lastMonth: number[];
	lastYear: number[];
	dateChanged: string;
	originalPeriods: string[];
};
export type Action = {
	type: string;
	payload?: any;
};

export const compareReportReducer: React.Reducer<
	CompareReportReducerState,
	Action
> = (state, action) => {
	switch (action.type) {
		case 'reset_filters': {
			return cloneDeep(initialState);
		}
		case 'update_unitIds':
			return {
				...state,
				filters: {
					...state.filters,
					unitIds: action.payload.isMulti
						? action.payload.value.map((val: any) => val.id || val)
						: [action.payload.value],
					dep: [],
					emp: [],
				},
			};
		case 'update_period':
			let periodNumber = 1;

			if (action.payload === Period.QUARTERLY) {
				periodNumber = getPeriodNumber(state.lastMonth[0]);
			}
			const dateFrom = getDateFrom(
				{
					year: state.filters.to_year[0],
					month: state.lastMonth[0],
				},
				action.payload,
				periodNumber,
			);

			return {
				...state,
				filters: {
					...state.filters,
					period: action.payload ? [action.payload] : [],
					to_year: [dateFrom.year],
					from_month: [dateFrom.month],
					from_year: [dateFrom.year],
					to_month:
						action.payload === Period.YEAR
							? [12]
							: action.payload === Period.QUARTERLY
							? [dateFrom.month + 2]
							: state.lastMonth,
					periods: [],
				},
			};
		case 'update_date_from': {
			const period = state.filters.period[0];
			const to_month = state.filters.to_month[0];
			const to_year = state.filters.to_year[0];

			let perNumber = 1;
			if (period === Period.QUARTERLY) {
				perNumber = getPeriodNumber(action.payload.getMonth() + 2);
			}

			const lastDate =
				period === Period.MONTH
					? moment(action.payload).add(11, 'month')
					: period === Period.QUARTERLY
					? moment(action.payload).add(35, 'month')
					: moment(action.payload).add(11, 'year');

			return {
				...state,
				filters: {
					...state.filters,
					from_year: [new Date(action.payload).getFullYear()],
					from_month: [new Date(action.payload).getMonth() + 1],
					to_year: [
						to_year > lastDate.year() ? lastDate.year() : to_year,
					],
					to_month: [
						to_year > lastDate.year() ||
						(to_year === lastDate.year() &&
							to_month > lastDate.month() + 1)
							? lastDate.month() + 1
							: to_month,
					],
					periods: [],
				},
				dateChanged: 'dateFrom',
			};
		}
		case 'update_date_to': {
			const period = state.filters.period[0];
			const from_month = state.filters.from_month[0];
			const from_year = state.filters.from_year[0];
			let perNumber = 1;
			if (period === Period.QUARTERLY) {
				perNumber = getPeriodNumber(action.payload.getMonth() + 1);
			}

			const firstDate =
				period === Period.MONTH
					? moment(action.payload).subtract(11, 'month')
					: period === Period.QUARTERLY
					? moment(action.payload).subtract(32, 'month')
					: moment(action.payload).subtract(11, 'year');

			return {
				...state,
				filters: {
					...state.filters,
					to_year: [new Date(action.payload).getFullYear()],
					to_month: [
						new Date(action.payload).getMonth() +
							(period === Period.QUARTERLY ? 3 : 1),
					],
					from_year: [
						from_year < firstDate.year()
							? firstDate.year()
							: from_year,
					],
					from_month: [
						from_year < firstDate.year() ||
						(from_year === firstDate.year() &&
							from_month < firstDate.month() + 1)
							? firstDate.month() + 1
							: from_month,
					],
					periods: [],
				},
				dateChanged: 'dateTo',
			};
		}
		case 'update_departments':
			return {
				...state,
				filters: {
					...state.filters,
					dep: action.payload.map((el: any) => el.id),
					emp: [],
				},
			};
		case 'update_employees':
			return {
				...state,
				filters: {
					...state.filters,
					emp: action.payload.map((el: any) => el.id),
				},
			};
		case 'update_periods':
			return {
				...state,
				filters: {
					...state.filters,
					periods: action.payload.map((el: any) => el.id),
				},
			};
		case 'set_departments_sort':
			return {
				...state,
				sortDepartmentsBy: Number(action.payload.field),
				departmentsSortDirection: action.payload.direction,
			};
		case 'set_employees_sort':
			return {
				...state,
				sortEmployeesBy: Number(action.payload.field),
				employeesSortDirection: action.payload.direction,
			};
		case 'set_selected_department':
			return {
				...state,
				selectedDepartmentForEmployeesFetch: [action.payload],
			};
		case 'update_role':
			return {
				...state,
				filters: {
					...state.filters,
					role: action.payload.map((el: any) => el.id),
				},
			};
		case 'update_status':
			return {
				...state,
				filters: { ...state.filters, status: [action.payload] },
			};
		case 'update_change_percent':
			return {
				...state,
				filters: { ...state.filters, change_percent: [action.payload] },
			};
		case 'fetch_departments_request':
			return {
				...state,
				completed: false,
			};
		case 'fetch_employees_request':
			return {
				...state,
				completed: false,
			};
		case 'fetch_departments_success':
			return {
				...state,
				errorMessage: '',
				completed: true,
				totalDepartmentsReports: action.payload.totalDepartmentsReports,
				periods: action.payload.periods,
				departments: action.payload.data,
				originalPeriods: action.payload.originalPeriods,
			};
		case 'fetch_employees_success':
			return {
				...state,
				errorMessage: '',
				completed: true,
				periods: action.payload.periods,
				employees: action.payload.data,
			};
		case 'fetch_report_last_date_request_success':
			const dateObjTo = parseLastDate(action.payload.date);
			const dateObjFrom = {
				month: MonthYearFormat(action.payload.date, -5, 'month', true),
				year: MonthYearFormat(action.payload.date, -5, 'year', true),
			};
			return {
				...state,
				filters: {
					...state.filters,
					from_year: [dateObjFrom.year],
					from_month: [dateObjFrom.month],
					to_year: [dateObjTo.year],
					to_month: [dateObjTo.month],
				},
				lastMonth: [dateObjTo.month],
				lastYear: [dateObjTo.year],
			};
		default:
			return state;
	}
};

export const fetchDepartments = ({
	sortBy,
	sortDirection,
	filter,
	dispatch,
}: any) => {
	dispatch({ type: 'fetch_departments_request' });
	httpService
		.api<any>({
			type: 'getCostCompareReportsList',
			data: {
				s: sortBy,
				sd: sortDirection,
				f: filter,
			},
		})
		.then(data => {
			dispatch({
				type: 'fetch_departments_success',
				payload: {
					totalDepartmentsReports: data.total,
					periods: data.periods,
					originalPeriods: data.original_periods,
					data: data.data.map(
						(report: any) => new CostCompareDepartmentModel(report),
					),
				},
			});
		});
	//.catch(err => dispatch({}));
};

export const fetchEmployees = ({
	sortBy,
	sortDirection,
	filter,
	dispatch,
}: any) => {
	dispatch({ type: 'fetch_employees_request' });
	httpService
		.api<any>({
			type: 'getCostCompareEmployeesReportsList',
			data: {
				s: sortBy,
				sd: sortDirection,
				f: filter,
			},
		})
		.then(data => {
			dispatch({
				type: 'fetch_employees_success',
				payload: {
					periods: data.periods,
					data: data.data.map(
						(report: any) => new CostCompareEmployeeModel(report),
					),
				},
			});
		});
	//.catch(err => dispatch({});
};

export const fetchExcel = async ({ sortBy, sortDirection, filter }: any) => {
	try {
		const res = await httpService.api<any>({
			type: 'getCompareCostReportsExcelFile',
			responseType: 'blob',
			returnAllRes: true,
			data: {
				s: sortBy,
				sd: sortDirection,
				f: filter,
			},
		});
		return res;
	} catch (e) {
		console.log(e);
	}
};

export const fetchReportLastDate = (
	dispatch: any,
	unitIds: number[] | string[],
) => {
	httpService
		.api<any>({
			type: 'getCostingReportLastDate',
			query: { unitIds: unitIds?.map(unit => `${unit}`).join() },
		})
		.then(data => {
			dispatch({
				type: 'fetch_report_last_date_request_success',
				payload: {
					date: data,
				},
			});
		});
};
